import React, { useState, useCallback } from 'react'
import { Button, Text } from '@flatfile/baseline'

export type FlatfileOSParams = {
  companyUrl?: string
}

type ConfigureFlatfileOSProps = {
  onSubmit: (params: FlatfileOSParams) => void
  error?: string
}

export const ConfigureFlatfileOSForm: React.FC<ConfigureFlatfileOSProps> = ({ onSubmit, error }) => {
  const [companyUrl, setCompanyUrl] = useState('')
  const [urlError, setUrlError] = useState<string | null>(null)

  const isValidUrl = (url: string): boolean => {
    try {
      // If it doesn't start with a protocol, add https://
      const urlToTest = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`
      new URL(urlToTest)
      return true
    } catch {
      return false
    }
  }

  const extractDomain = (url: string): string => {
    try {
      // If it doesn't start with a protocol, add https://
      const urlToProcess = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`

      const urlObj = new URL(urlToProcess)
      // Remove www. prefix if it exists
      return urlObj.hostname.replace(/^www\./, '')
    } catch {
      // If URL parsing fails, try to extract domain manually
      const cleaned = url
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .split('?')[0]
      return cleaned
    }
  }

  const handleSubmit = useCallback(() => {
    const trimmedUrl = companyUrl.trim()

    if (!trimmedUrl) {
      setUrlError('Company URL is required.')
      return
    }

    if (!isValidUrl(trimmedUrl)) {
      setUrlError('Please enter a valid URL or domain name.')
      return
    }

    // Extract just the domain from the URL
    const domain = extractDomain(trimmedUrl)

    setUrlError(null)
    onSubmit({ companyUrl: domain })
  }, [companyUrl, onSubmit])

  const handleUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCompanyUrl(e.target.value)
      if (urlError) {
        setUrlError(null)
      }
    },
    [urlError]
  )

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  return (
    <div className='relative w-full flex flex-col h-[calc(100vh-64px)]'>
      {/* Main section */}
      <div className='w-full h-full min-h-[480px] flex items-center justify-center overflow-y-auto'>
        <div className='bg-popover w-[660px] max-w-[90%] p-5 rounded-md shadow-sm mx-auto my-8'>
          <div className='flex flex-col w-full items-center'>
            <Text variant='largeTitle' emphasized className='mb-6 text-center font-normal'>
              Build your app with Flatfile OS
            </Text>

            <div className='w-full mb-6'>
              <Text variant='body' className='mb-3 text-left font-medium'>
                Company URL
              </Text>
              <input
                type='text'
                className='w-full border border-border-input rounded-md p-3 h-12 focus:outline-none focus:ring-primary focus:ring-1 text-sm bg-base-white'
                placeholder='company.com'
                value={companyUrl}
                onChange={handleUrlChange}
                onKeyPress={handleKeyPress}
              />

              {urlError && (
                <Text variant='callout' className='mt-2 text-destructive'>
                  {urlError}
                </Text>
              )}
            </div>

            {/* Action button */}
            <div className='mt-6 flex justify-center w-full'>
              <Button
                variant='primary'
                onClick={handleSubmit}
                disabled={!companyUrl.trim()}
                icon='arrow-right'
                iconPlacement='right'
                iconProps={{ size: 12 }}
              >
                Build with Flatfile OS
              </Button>
            </div>

            {error && (
              <Text variant='body' className='mt-4 text-destructive text-center'>
                {error}
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
